import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Warning,
  Trash,
  CloudX,
  CheckSquare,
  Square,
  SignOut,
  FloppyDisk,
  MicrosoftOutlookLogo,
  ClipboardText,
  Plugs,
  Robot,
  GearSix,
  SlidersHorizontal,
  Plus,
  X,
  ArrowCounterClockwise,
  FolderSimple,
  TreeStructure,
} from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { wipeAccount, getAccountSettings, saveAccountSettings, listOrganizations, updateProfile, previewOneDriveFolderReset, resetOneDriveFolder } from "../api/settings";
import { getMyJoinRequests, submitJoinRequest } from "../api/admin";
import { getMsStatus } from "../api/microsoft";
import { getOnboardingConfig, updateOnboardingConfig, getOnboardingPresets, createOnboardingPreset } from "../api/onboarding";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { IntegrationsContent } from "./IntegrationsPage";
import { AutomationsContent } from "./AIAutomationsPage";
import FolderLayoutsSettings from "../components/settings/FolderLayoutsSettings";

const TABS = [
  { id: "general",      label: "General",        icon: GearSix },
  { id: "layouts",      label: "Folder Layouts", icon: TreeStructure },
  { id: "advanced",     label: "Advanced",       icon: SlidersHorizontal },
  { id: "integrations", label: "Integrations",   icon: Plugs },
  { id: "automations",  label: "AI Automations", icon: Robot },
];

export default function SettingsPage() {
  const { user, signOut, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "general";

  // ── Microsoft account info ──────────────────────────────────────────────
  const [msInfo, setMsInfo] = useState(null);
  useEffect(() => {
    getMsStatus()
      .then((s) => { if (s.connected) setMsInfo(s); })
      .catch(() => {});
  }, []);

  // ── Account settings ────────────────────────────────────────────────────
  const [settings, setSettings]         = useState({ max_emails_to_process: 20 });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [saving, setSaving]             = useState(false);

  // ── Profile (name + one-time organization pick) ────────────────────────
  // super_admin/admin accounts are global — never scoped to an organization.
  const isSuperAdminUser = user?.role === "super_admin" || user?.role === "admin";
  const orgLocked = Boolean(user?.organization_id);
  const [profileName, setProfileName]   = useState("");
  const [profileOrgId, setProfileOrgId] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [profileSaving, setProfileSaving] = useState(false);

  // ── Join Request state ──────────────────────────────────────────────────
  const [joinOrgName, setJoinOrgName] = useState("");
  const [joiningOrg, setJoiningOrg] = useState(false);
  const [myJoinRequests, setMyJoinRequests] = useState([]);

  useEffect(() => {
    if (user) {
      setProfileName(user.name || "");
      setProfileOrgId(user.organization_id || "");
    }
  }, [user]);

  useEffect(() => {
    // Only need the org list while the user hasn't picked one yet (and is
    // eligible to pick one at all — super admins never are).
    if (user && !user.organization_id && !isSuperAdminUser) {
      listOrganizations().then(setOrganizations).catch(() => {});
      getMyJoinRequests().then(setMyJoinRequests).catch(() => {});
    }
  }, [user?.organization_id, isSuperAdminUser]);

  const handleJoinOrgRequest = async () => {
    if (!joinOrgName.trim()) {
      toast.error("Please enter an organization name");
      return;
    }
    setJoiningOrg(true);
    try {
      const result = await submitJoinRequest(joinOrgName.trim());
      setMyJoinRequests([result, ...myJoinRequests]);
      setJoinOrgName("");
      toast.success("Join request submitted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit join request");
    } finally {
      setJoiningOrg(false);
    }
  };

  const handleSaveProfile = async () => {
    const trimmedName = profileName.trim();
    if (!trimmedName) {
      toast.error("Name cannot be empty.");
      return;
    }
    setProfileSaving(true);
    try {
      const payload = { name: trimmedName };
      if (!isSuperAdminUser && !orgLocked && profileOrgId) payload.organization_id = profileOrgId;
      const result = await updateProfile(payload);
      updateUser(result);
      toast.success("Account details saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save account details.");
    } finally {
      setProfileSaving(false);
    }
  };

  useEffect(() => {
    getAccountSettings()
      .then(setSettings)
      .catch(() => {})
      .finally(() => setSettingsLoading(false));
  }, []);

  // ── Onboarding config ───────────────────────────────────────────────────
  const [onboardingConfig, setOnboardingConfig] = useState(null);
  const [onboardingPresets, setOnboardingPresets] = useState([]);
  const [onboardingLoading, setOnboardingLoading] = useState(true);
  const [onboardingSaving, setOnboardingSaving] = useState(false);
  const [presetCreatorOpen, setPresetCreatorOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [presetRequirements, setPresetRequirements] = useState([""]);
  const [presetSaving, setPresetSaving] = useState(false);

  useEffect(() => {
    Promise.all([getOnboardingConfig(), getOnboardingPresets()])
      .then(([config, presets]) => {
        setOnboardingConfig(config);
        setOnboardingPresets(presets);
      })
      .catch(() => {})
      .finally(() => setOnboardingLoading(false));
  }, []);

  const handleOnboardingModeChange = async (newMode) => {
    const currentPresetIsSelectable = onboardingConfig?.preset_id && onboardingConfig.preset_id !== "ai_inferred";
    const defaultPresetId = onboardingPresets.find((preset) => preset.id !== "ai_inferred")?.id || "standard_construction";
    const presetId = newMode === "preset" ? (currentPresetIsSelectable ? onboardingConfig.preset_id : defaultPresetId)
      : newMode === "ai_inferred" ? "ai_inferred"
      : null;

    const payload = {
      mode: newMode,
      preset_id: presetId,
      custom_requirements: newMode === "custom" ? (onboardingConfig?.custom_requirements || []) : [],
    };

    setOnboardingSaving(true);
    try {
      const saved = await updateOnboardingConfig(payload);
      setOnboardingConfig(saved);
      toast.success("Onboarding configuration saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save onboarding config");
    } finally {
      setOnboardingSaving(false);
    }
  };

  const handlePresetChange = async (presetId) => {
    const payload = {
      mode: "preset",
      preset_id: presetId,
      custom_requirements: [],
    };
    setOnboardingSaving(true);
    try {
      const saved = await updateOnboardingConfig(payload);
      setOnboardingConfig(saved);
      toast.success("Preset updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update preset");
    } finally {
      setOnboardingSaving(false);
    }
  };

  const handleCreatePreset = async () => {
    const labels = presetRequirements.map((label) => label.trim()).filter(Boolean);
    if (!presetName.trim()) {
      toast.error("Enter a preset name");
      return;
    }
    if (labels.length === 0) {
      toast.error("Add at least one requirement");
      return;
    }

    setPresetSaving(true);
    try {
      const created = await createOnboardingPreset({
        name: presetName.trim(),
        description: "User-created onboarding preset",
        requirements: labels.map((label, index) => ({
          id: `custom_requirement_${Date.now()}_${index}`,
          label,
          category: "general",
          file_keywords: [label.toLowerCase()],
          field_match: null,
          required: true,
          notes: null,
        })),
      });
      setOnboardingPresets((presets) => [...presets, created]);
      const saved = await updateOnboardingConfig({
        mode: "preset",
        preset_id: created.id,
        custom_requirements: [],
      });
      setOnboardingConfig(saved);
      setPresetName("");
      setPresetRequirements([""]);
      setPresetCreatorOpen(false);
      toast.success("Preset created and selected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create preset");
    } finally {
      setPresetSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const saved = await saveAccountSettings(settings);
      setSettings(saved);
      toast.success("Settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  // OneDrive folder reset dialog state
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetPreview, setResetPreview] = useState(null);
  const [resetPreviewLoading, setResetPreviewLoading] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resettingFolder, setResettingFolder] = useState(false);

  const openResetDialog = async () => {
    setShowResetDialog(true);
    setResetPreviewLoading(true);
    setResetPreview(null);
    setResetConfirmText("");
    try {
      setResetPreview(await previewOneDriveFolderReset());
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Unknown error";
      toast.error(`Could not prepare reset: ${msg}`);
      setShowResetDialog(false);
    } finally {
      setResetPreviewLoading(false);
    }
  };

  const closeResetDialog = () => {
    if (resettingFolder) return;
    setShowResetDialog(false);
    setResetPreview(null);
    setResetConfirmText("");
  };

  const handleResetFolder = async () => {
    const confirmed = resetConfirmText.trim().toUpperCase() === "RESET";
    if (!confirmed || !resetPreview?.confirmation_token || resettingFolder) return;
    setResettingFolder(true);
    try {
      await resetOneDriveFolder(resetPreview.confirmation_token);
      toast.success("OneDrive folder reset complete.", {
        description: "A fresh DD Project Planner folder with Completed Projects is ready. Your integrations, projects, settings, and chat history were preserved.",
        duration: 7000,
      });
      setShowResetDialog(false);
      setResetPreview(null);
      setResetConfirmText("");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Unknown error";
      toast.error(`Folder reset failed: ${msg}`);
    } finally {
      setResettingFolder(false);
    }
  };

  // Wipe dialog state
  const [showWipeDialog, setShowWipeDialog] = useState(false);
  const [wipeOnedrive, setWipeOnedrive]     = useState(false);
  const [confirmText, setConfirmText]       = useState("");
  const [wiping, setWiping]                 = useState(false);

  const canConfirm = confirmText.trim().toUpperCase() === "WIPE";

  const handleWipe = async () => {
    if (!canConfirm || wiping) return;
    setWiping(true);
    try {
      const result = await wipeAccount(wipeOnedrive);
      toast.success(`Account wiped — ${result.total_deleted} records deleted.`, {
        description: wipeOnedrive
          ? "All data, integrations, and your OneDrive project folder have been deleted."
          : "All data and integrations have been disconnected. Your OneDrive folder is untouched.",
        duration: 6000,
      });
      setShowWipeDialog(false);
      // Send back to projects — it will be empty
      navigate("/");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Unknown error";
      toast.error(`Wipe failed: ${msg}`);
    } finally {
      setWiping(false);
    }
  };

  const closeDialog = () => {
    if (wiping) return;
    setShowWipeDialog(false);
    setConfirmText("");
    setWipeOnedrive(false);
  };

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-8 py-5 flex-shrink-0">
        <h1 className="font-cabinet text-xl font-black tracking-tight text-slate-900">
          Settings
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Manage your account, integrations, and automations.
        </p>

        {/* Tabs */}
        <div className="flex items-center gap-1 mt-4 -mb-[21px]">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setSearchParams({ tab: tab.id })}
                data-testid={`settings-tab-${tab.id}`}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-t-md border-b-2 transition-colors ${
                  active
                    ? "border-primary text-primary bg-primary/5"
                    : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                }`}
              >
                <Icon size={14} weight={active ? "bold" : "regular"} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === "layouts" && <FolderLayoutsSettings />}

      {activeTab === "integrations" && (
        <div className="px-8 py-6 max-w-5xl">
          <IntegrationsContent />
        </div>
      )}

      {activeTab === "automations" && (
        <div className="px-8 py-6 max-w-5xl">
          <AutomationsContent />
        </div>
      )}

      {activeTab === "advanced" && (
        <div className="px-8 py-6 max-w-3xl space-y-8" data-testid="settings-advanced-page">
          <section>
            <h2 className="text-xs font-semibold tracking-[0.12em] text-slate-400 uppercase mb-3">
              Email Automation
            </h2>
            <div className="bg-white border border-slate-200 rounded-lg px-5 py-4 space-y-4">
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-800">Max emails to scan per backfill</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                    Limit how many matching emails AI processes when a project inbox scan runs.
                  </p>
                </div>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={settings.max_emails_to_process}
                  disabled={settingsLoading}
                  onWheel={(e) => e.target.blur()}
                  onChange={(e) => setSettings((current) => ({
                    ...current,
                    max_emails_to_process: Math.min(100, Math.max(1, parseInt(e.target.value) || 1)),
                  }))}
                  data-testid="max-emails-input"
                  className={`w-20 flex-shrink-0 border rounded-md px-3 py-1.5 text-sm text-center font-medium focus:outline-none focus:ring-2 disabled:opacity-50 ${
                    settings.max_emails_to_process > 30
                      ? "border-amber-400 focus:ring-amber-400 text-amber-700"
                      : "border-slate-300 focus:ring-primary"
                  }`}
                />
              </div>
              {settings.max_emails_to_process > 30 && (
                <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800">
                  <Warning size={14} weight="fill" className="flex-shrink-0 mt-0.5 text-amber-500" />
                  <span><strong>Large scan:</strong> this can significantly increase processing time and AI usage.</span>
                </div>
              )}
              <div className="flex justify-end">
                <button
                  onClick={handleSaveSettings}
                  disabled={saving || settingsLoading}
                  data-testid="save-settings-btn"
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-md disabled:opacity-50"
                >
                  <FloppyDisk size={14} weight="bold" />
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold tracking-[0.12em] text-slate-400 uppercase">
                Onboarding Requirements
              </h2>
              <button
                type="button"
                onClick={() => setPresetCreatorOpen((open) => !open)}
                data-testid="create-onboarding-preset-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary border border-primary/25 bg-primary/5 hover:bg-primary/10 rounded-md"
              >
                <Plus size={13} weight="bold" /> Create Preset
              </button>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg px-5 py-4 space-y-4">
              {onboardingLoading ? (
                <p className="text-xs text-slate-400">Loading...</p>
              ) : (
                <>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                      <ClipboardText size={14} className="text-slate-500" />
                      Project Onboarding Mode
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      AI-inferred is the default for new accounts. Choose a preset for a fixed reusable checklist.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {[
                      { value: "ai_inferred", label: "AI-inferred" },
                      { value: "preset", label: "Preset" },
                      { value: "custom", label: "Custom" },
                      { value: "none", label: "None" },
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleOnboardingModeChange(option.value)}
                        disabled={onboardingSaving}
                        data-testid={`onboarding-mode-${option.value}`}
                        className={`text-xs font-semibold px-3 py-2 rounded border transition-colors ${
                          onboardingConfig?.mode === option.value
                            ? "bg-primary/10 border-primary text-primary"
                            : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                        } disabled:opacity-50`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>

                  {onboardingConfig?.mode === "preset" && (
                    <div className="space-y-3">
                      <Select value={onboardingConfig?.preset_id || ""} onValueChange={handlePresetChange}>
                        <SelectTrigger className="w-full sm:w-80" data-testid="onboarding-preset-select">
                          <SelectValue placeholder="Select preset" />
                        </SelectTrigger>
                        <SelectContent>
                          {onboardingPresets.filter((preset) => preset.id !== "ai_inferred").map((preset) => (
                            <SelectItem key={preset.id} value={preset.id}>
                              {preset.name}{preset.is_custom ? " · Yours" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {(() => {
                        const selected = onboardingPresets.find((preset) => preset.id === onboardingConfig?.preset_id);
                        if (!selected?.requirements?.length) return null;
                        return (
                          <div className="bg-slate-50 border border-slate-100 rounded-md p-3 space-y-1">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
                              Requirements ({selected.requirements.length})
                            </p>
                            {selected.requirements.map((requirement) => (
                              <div key={requirement.id} className="flex items-center gap-2 text-xs text-slate-700">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary/50" />
                                {requirement.label}
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {onboardingConfig?.mode === "ai_inferred" && (
                    <div className="bg-blue-50 border border-blue-100 rounded-md p-3 text-xs text-blue-700">
                      AI analyses each project and suggests likely missing documents dynamically.
                    </div>
                  )}
                </>
              )}

              {presetCreatorOpen && (
                <div className="border-t border-slate-100 pt-4 space-y-3" data-testid="onboarding-preset-creator">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-800">Create your preset</p>
                    <button type="button" onClick={() => setPresetCreatorOpen(false)} className="p-1 text-slate-400 hover:text-slate-700" aria-label="Close preset creator">
                      <X size={15} />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={presetName}
                    onChange={(e) => setPresetName(e.target.value)}
                    placeholder="Preset name"
                    data-testid="preset-name-input"
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <div className="space-y-2">
                    {presetRequirements.map((requirement, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={requirement}
                          onChange={(e) => setPresetRequirements((items) => items.map((item, itemIndex) => itemIndex === index ? e.target.value : item))}
                          placeholder={`Requirement ${index + 1}`}
                          data-testid={`preset-requirement-${index}`}
                          className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        {presetRequirements.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setPresetRequirements((items) => items.filter((_, itemIndex) => itemIndex !== index))}
                            className="p-2 text-slate-400 hover:text-red-600"
                            aria-label={`Remove requirement ${index + 1}`}
                          >
                            <X size={15} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setPresetRequirements((items) => [...items, ""])}
                      className="flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-primary"
                    >
                      <Plus size={12} weight="bold" /> Add requirement
                    </button>
                    <button
                      type="button"
                      onClick={handleCreatePreset}
                      disabled={presetSaving}
                      data-testid="save-onboarding-preset-btn"
                      className="px-4 py-2 text-xs font-semibold text-white bg-primary hover:bg-primary/90 rounded-md disabled:opacity-50"
                    >
                      {presetSaving ? "Creating…" : "Create and select"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {activeTab === "general" && (
      <div className="px-8 py-6 max-w-2xl space-y-8">

        {/* Account info */}
        <section>
          <h2 className="text-xs font-semibold tracking-[0.12em] text-slate-400 uppercase mb-3">
            Account
          </h2>
          <div className="bg-white border border-slate-200 rounded-lg px-5 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Email</span>
              <span className="text-sm font-medium text-slate-800" data-testid="settings-user-email">
                {user?.email}
              </span>
            </div>

            <div className="border-t border-slate-100" />
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-slate-500 flex-shrink-0">Name</span>
              <input
                type="text"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                placeholder="Your full name"
                data-testid="settings-name-input"
                className="w-56 text-right border border-slate-300 rounded-md px-3 py-1.5 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>

            {!isSuperAdminUser && (
              <>
                <div className="border-t border-slate-100" />
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-slate-500 flex-shrink-0">Organization</span>
                  {orgLocked ? (
                    <span className="text-sm font-medium text-slate-800" data-testid="settings-org-readonly">
                      {user?.organization_name || "—"}
                    </span>
                  ) : (
                    <Select value={profileOrgId} onValueChange={setProfileOrgId}>
                      <SelectTrigger className="w-56" data-testid="settings-org-select">
                        <SelectValue placeholder="Select organization" />
                      </SelectTrigger>
                      <SelectContent>
                        {organizations.map((org) => (
                          <SelectItem key={org.id} value={org.id}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                {!orgLocked && (
                  <p className="text-xs text-slate-400 -mt-1">
                    You can only set your organization once — choose carefully.
                  </p>
                )}
              </>
            )}

            {msInfo?.ms_account_email && (
              <>
                <div className="border-t border-slate-100" />
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <MicrosoftOutlookLogo size={13} weight="fill" className="text-[#0078D4]" />
                    <span className="text-sm">Microsoft account</span>
                  </div>
                  <div className="flex flex-col items-end">
                    {msInfo.ms_display_name && (
                      <span className="text-[11px] font-semibold text-slate-700">{msInfo.ms_display_name}</span>
                    )}
                    <span className="text-sm font-medium text-slate-800" data-testid="settings-ms-account-email">
                      {msInfo.ms_account_email}
                    </span>
                  </div>
                </div>
              </>
            )}
            <div className="border-t border-slate-100" />
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Workspace created</span>
              <span className="text-sm text-slate-600">
                {user?.created_at
                  ? new Date(user.created_at).toLocaleDateString("en-AU", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })
                  : "—"}
              </span>
            </div>

            <div className="border-t border-slate-100" />
            <div className="flex justify-end pt-1">
              <button
                onClick={handleSaveProfile}
                disabled={profileSaving}
                data-testid="save-account-btn"
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {profileSaving ? (
                  <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                  <FloppyDisk size={14} weight="bold" />
                )}
                {profileSaving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </section>

        {/* Join Organization — only for users without an org */}
        {!isSuperAdminUser && !user?.organization_id && (
          <section>
            <h2 className="text-xs font-semibold tracking-[0.12em] text-slate-400 uppercase mb-3">
              Organization
            </h2>
            <div className="bg-white border border-slate-200 rounded-lg px-5 py-4 space-y-4">
              {myJoinRequests.filter((jr) => jr.status === "pending").length > 0 ? (
                <div>
                  <p className="text-sm text-slate-700 font-medium mb-2">Pending Request</p>
                  {myJoinRequests.filter((jr) => jr.status === "pending").map((jr) => (
                    <div key={jr.id} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-md px-4 py-2">
                      <span className="text-sm text-amber-800">
                        Request pending for <strong>{jr.organization_name}</strong>
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Request to join an organization
                    </label>
                    <input
                      type="text"
                      value={joinOrgName}
                      onChange={(e) => setJoinOrgName(e.target.value)}
                      placeholder="Enter organization name"
                      data-testid="join-org-input"
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <button
                    onClick={handleJoinOrgRequest}
                    disabled={joiningOrg || !joinOrgName.trim()}
                    data-testid="join-org-btn"
                    className="px-4 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {joiningOrg ? "Submitting..." : "Request to Join"}
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Email Automation */}
        <section>
          <h2 className="text-xs font-semibold tracking-[0.12em] text-slate-400 uppercase mb-3">
            Notifications
          </h2>
          <div className="bg-white border border-slate-200 rounded-lg px-5 py-4 space-y-4">
            <div className="flex items-start justify-between gap-6">
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Play sound when AI work completes</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                  When enabled, a short chime plays in the browser when a long-running
                  evaluation, comparison, or analysis finishes and a new notification arrives.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  const current = localStorage.getItem("dd_notification_sound_enabled") === "true";
                  localStorage.setItem("dd_notification_sound_enabled", String(!current));
                  // Force re-render
                  setSettings((s) => ({ ...s }));
                  toast.success(!current ? "Sound notifications enabled" : "Sound notifications disabled");
                }}
                data-testid="notification-sound-toggle"
                className={`flex-shrink-0 w-11 h-6 rounded-full relative transition-colors ${
                  localStorage.getItem("dd_notification_sound_enabled") === "true"
                    ? "bg-primary"
                    : "bg-slate-300"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    localStorage.getItem("dd_notification_sound_enabled") === "true"
                      ? "translate-x-5"
                      : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          </div>
        </section>

        {/* Advanced: Email Automation is rendered on the Advanced tab. */}
        <section className="hidden" aria-hidden="true">
          <h2 className="text-xs font-semibold tracking-[0.12em] text-slate-400 uppercase mb-3">
            Email Automation
          </h2>
          <div className="bg-white border border-slate-200 rounded-lg px-5 py-4 space-y-4">
            <div className="flex items-start justify-between gap-6">
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Max emails to scan per backfill</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                  When a project is created or you click “Scan inbox”, the AI will
                  search the inbox and process at most this many matching emails.
                  Higher values are more thorough but take longer.
                </p>
              </div>
              <input
                type="number"
                min={1}
                max={100}
                value={settings.max_emails_to_process}
                disabled={settingsLoading}
                onWheel={(e) => e.target.blur()}
                onChange={(e) =>
                  setSettings((s) => ({
                    ...s,
                    max_emails_to_process: Math.min(100, Math.max(1, parseInt(e.target.value) || 1)),
                  }))
                }
                data-testid="max-emails-input"
                className={`w-20 flex-shrink-0 border rounded-md px-3 py-1.5 text-sm text-center font-medium focus:outline-none focus:ring-2 disabled:opacity-50 ${
                  settings.max_emails_to_process > 30
                    ? "border-amber-400 focus:ring-amber-400 focus:border-amber-400 text-amber-700"
                    : "border-slate-300 focus:ring-primary focus:border-primary"
                }`}
              />
            </div>

            {settings.max_emails_to_process > 30 && (
              <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800">
                <Warning size={14} weight="fill" className="flex-shrink-0 mt-0.5 text-amber-500" />
                <span>
                  <span className="font-semibold">Large scan — this may take a while.</span>{" "}
                  Scanning more than 30 emails per address can significantly increase processing time
                  and AI token usage. Consider whether a lower value meets your needs.
                </span>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={handleSaveSettings}
                disabled={saving || settingsLoading}
                data-testid="save-settings-btn"
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                  <FloppyDisk size={14} weight="bold" />
                )}
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </section>

        {/* Advanced: Onboarding Requirements are rendered on the Advanced tab. */}
        <section className="hidden" aria-hidden="true">
          <h2 className="text-xs font-semibold tracking-[0.12em] text-slate-400 uppercase mb-3">
            Onboarding Requirements
          </h2>
          <div className="bg-white border border-slate-200 rounded-lg px-5 py-4 space-y-4">
            {onboardingLoading ? (
              <p className="text-xs text-slate-400">Loading...</p>
            ) : (
              <>
                <div className="flex items-start justify-between gap-6">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                      <ClipboardText size={14} className="text-slate-500" />
                      Project Onboarding Mode
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                      Choose how onboarding requirements are determined for new projects.
                      A preset provides a fixed checklist; AI-inferred mode dynamically suggests missing documents.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[
                    { value: "preset", label: "Preset" },
                    { value: "ai_inferred", label: "AI-inferred" },
                    { value: "custom", label: "Custom" },
                    { value: "none", label: "None" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => handleOnboardingModeChange(opt.value)}
                      disabled={onboardingSaving}
                      className={`text-xs font-semibold px-3 py-2 rounded border transition-colors ${
                        onboardingConfig?.mode === opt.value
                          ? "bg-primary/10 border-primary text-primary"
                          : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                      } disabled:opacity-50`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {onboardingConfig?.mode === "preset" && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-medium text-slate-600">Preset:</span>
                      <Select
                        value={onboardingConfig?.preset_id || "standard_construction"}
                        onValueChange={handlePresetChange}
                      >
                        <SelectTrigger className="w-64" data-testid="onboarding-preset-select">
                          <SelectValue placeholder="Select preset" />
                        </SelectTrigger>
                        <SelectContent>
                          {onboardingPresets
                            .filter((p) => p.id !== "ai_inferred")
                            .map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Show preset requirements as read-only list */}
                    {(() => {
                      const selectedPreset = onboardingPresets.find(
                        (p) => p.id === (onboardingConfig?.preset_id || "standard_construction")
                      );
                      if (!selectedPreset?.requirements?.length) return null;
                      return (
                        <div className="bg-slate-50 border border-slate-100 rounded p-3">
                          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
                            Requirements ({selectedPreset.requirements.length})
                          </p>
                          <div className="space-y-1">
                            {selectedPreset.requirements.map((r) => (
                              <div key={r.id} className="flex items-center gap-2 text-xs text-slate-700">
                                <span className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                                <span>{r.label}</span>
                                <span className="text-[10px] text-slate-400 ml-auto">{r.category}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {onboardingConfig?.mode === "ai_inferred" && (
                  <div className="bg-blue-50 border border-blue-100 rounded p-3">
                    <p className="text-xs text-blue-700">
                      AI will analyse each project individually and suggest what documents are likely
                      needed based on project type, contractors, and industry standards.
                    </p>
                  </div>
                )}

                {onboardingConfig?.mode === "custom" && (
                  <div className="bg-amber-50 border border-amber-100 rounded p-3">
                    <p className="text-xs text-amber-700">
                      Custom mode allows you to define your own requirements. This can be configured
                      per-project via the project assistant or API.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* Danger zone */}
        <section>
          <h2 className="text-xs font-semibold tracking-[0.12em] text-red-400 uppercase mb-3">
            Danger Zone
          </h2>
          <div className="bg-white border border-red-200 rounded-lg divide-y divide-slate-100">
            <div className="px-5 py-4 flex items-start justify-between gap-6">
              <div>
                <p className="text-sm font-semibold text-slate-800">Reset OneDrive Folder</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                  Delete and recreate the app&apos;s <span className="font-mono text-slate-700">DD Project Planner</span> folder
                  with a fresh Completed Projects folder. Your account, projects,
                  integrations, settings, and chat history stay unchanged.
                </p>
              </div>
              <button
                onClick={openResetDialog}
                data-testid="reset-onedrive-folder-btn"
                className="flex-shrink-0 flex items-center gap-2 px-4 py-2 text-sm font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-md transition-colors"
              >
                <ArrowCounterClockwise size={15} weight="bold" />
                Reset Folder
              </button>
            </div>

            <div className="px-5 py-4 flex items-start justify-between gap-6">
              <div>
                <p className="text-sm font-semibold text-slate-800">Wipe Account</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                  Permanently delete all projects, automations, email history,
                  AI training logs, drawing versions, and notifications. This
                  cannot be undone.
                </p>
              </div>
              <button
                onClick={() => setShowWipeDialog(true)}
                data-testid="wipe-account-btn"
                className="flex-shrink-0 flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 active:bg-red-800 rounded-md transition-colors"
              >
                <Trash size={15} weight="bold" />
                Wipe Account
              </button>
            </div>

            <div className="px-5 py-4 flex items-start justify-between gap-6">
              <div>
                <p className="text-sm font-semibold text-slate-800">Log Out</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  End your current session and return to the login screen.
                </p>
              </div>
              <button
                onClick={signOut}
                data-testid="settings-logout-btn"
                className="flex-shrink-0 flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
              >
                <SignOut size={15} weight="bold" />
                Log Out
              </button>
            </div>
          </div>
        </section>
      </div>
      )}

      {/* ── OneDrive folder reset confirmation dialog ─────────────────────── */}
      {showResetDialog && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="reset-onedrive-dialog-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) closeResetDialog(); }}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-lg"
            data-testid="reset-onedrive-dialog"
          >
            <div className="px-6 pt-6 pb-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-red-50 rounded-full flex items-center justify-center flex-shrink-0">
                  <FolderSimple size={18} weight="fill" className="text-red-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-900">Reset OneDrive folder?</h3>
                  <p className="text-xs text-slate-500 mt-0.5">The old folder moves to the OneDrive recycle bin.</p>
                </div>
              </div>
            </div>

            <div className="px-6 py-5 space-y-4">
              {resetPreviewLoading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500" data-testid="reset-onedrive-preview-loading">
                  <span className="w-4 h-4 border-2 border-slate-300 border-t-red-600 rounded-full animate-spin" />
                  Inspecting OneDrive folder…
                </div>
              ) : resetPreview && (
                <>
                  <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-3 text-xs text-red-800 space-y-2">
                    <p className="font-semibold">This will permanently remove from its current location:</p>
                    <div className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-red-400 flex-shrink-0 mt-1.5" />
                      <span>
                        The entire <span className="font-mono font-semibold">DD Project Planner</span> folder and all files inside it
                        {resetPreview.folder_exists
                          ? ` (${resetPreview.immediate_child_count} immediate item${resetPreview.immediate_child_count === 1 ? "" : "s"} detected)`
                          : " (the folder is currently missing, so a fresh one will be created)"}
                      </span>
                    </div>
                  </div>

                  <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-3 text-xs text-emerald-800 space-y-1.5">
                    <p className="font-semibold mb-2">The following stay unchanged:</p>
                    {(resetPreview.preserved || []).map((item) => (
                      <div key={item} className="flex items-center gap-2">
                        <CheckSquare size={13} weight="fill" className="text-emerald-600 flex-shrink-0" />
                        {item}
                      </div>
                    ))}
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800 leading-relaxed">
                    Existing project records are preserved exactly as requested. Their old OneDrive folder links may no longer open after the folder is reset.
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">
                      Type <span className="font-mono font-bold text-slate-800">RESET</span> to confirm
                    </label>
                    <input
                      type="text"
                      value={resetConfirmText}
                      onChange={(e) => setResetConfirmText(e.target.value)}
                      placeholder="Type RESET here"
                      data-testid="reset-onedrive-confirm-input"
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      autoComplete="off"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="px-6 pb-6 flex items-center gap-3">
              <button
                onClick={closeResetDialog}
                disabled={resettingFolder}
                data-testid="reset-onedrive-cancel-btn"
                className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleResetFolder}
                disabled={resetConfirmText.trim().toUpperCase() !== "RESET" || !resetPreview?.confirmation_token || resettingFolder}
                data-testid="reset-onedrive-confirm-btn"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {resettingFolder ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Resetting…
                  </>
                ) : (
                  <>
                    <ArrowCounterClockwise size={14} weight="bold" />
                    Reset Folder
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Wipe confirmation dialog ──────────────────────────────────────── */}
      {showWipeDialog && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="wipe-dialog-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) closeDialog(); }}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md"
            data-testid="wipe-dialog"
          >
            {/* Dialog header */}
            <div className="px-6 pt-6 pb-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-red-50 rounded-full flex items-center justify-center flex-shrink-0">
                  <Warning size={18} weight="fill" className="text-red-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-900">Wipe account data?</h3>
                  <p className="text-xs text-slate-500 mt-0.5">This action is permanent and cannot be undone.</p>
                </div>
              </div>
            </div>

            {/* Dialog body */}
            <div className="px-6 py-5 space-y-4">
              {/* What gets deleted */}
              <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-3 text-xs text-red-800 space-y-1.5">
                <p className="font-semibold mb-2">The following will be permanently deleted:</p>
                {[
                  "All projects and project files",
                  "All automations and webhook tokens",
                  "Email routing history and AI training logs",
                  "Drawing versions and evaluation history",
                  "All notifications",
                  "All connected integrations (Microsoft, Outlook, OneDrive)",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-red-400 flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>

              {/* OneDrive optional checkbox */}
              <button
                type="button"
                onClick={() => setWipeOnedrive(!wipeOnedrive)}
                data-testid="wipe-onedrive-checkbox"
                className="w-full flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors text-left"
              >
                {wipeOnedrive
                  ? <CheckSquare size={18} weight="fill" className="text-red-600 flex-shrink-0 mt-0.5" />
                  : <Square size={18} className="text-slate-400 flex-shrink-0 mt-0.5" />
                }
                <div>
                  <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                    <CloudX size={14} className="text-slate-500" />
                    Also delete OneDrive folder
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Permanently delete the <span className="font-mono text-slate-700">DD Project Planner</span> folder
                    and everything inside it from your Microsoft OneDrive.
                  </p>
                </div>
              </button>

              {/* Confirm input */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Type <span className="font-mono font-bold text-slate-800">WIPE</span> to confirm
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Type WIPE here"
                  data-testid="wipe-confirm-input"
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  autoComplete="off"
                />
              </div>
            </div>

            {/* Dialog footer */}
            <div className="px-6 pb-6 flex items-center gap-3">
              <button
                onClick={closeDialog}
                disabled={wiping}
                data-testid="wipe-cancel-btn"
                className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleWipe}
                disabled={!canConfirm || wiping}
                data-testid="wipe-confirm-btn"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {wiping ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Wiping…
                  </>
                ) : (
                  <>
                    <Trash size={14} weight="bold" />
                    Wipe Everything
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
